import { _ } from "i18n";
import DeleteIcon from "ui-kit/assets/delete/delete.svg";
import SaveIcon from "ui-kit/assets/save/save.svg";
import type { LinkEntry } from "./discoverGameLinks";
import "./DiscoverGameLinksFields.css";

interface DiscoverGameLinksFieldsProps {
	teamsheetLinks: LinkEntry[];
	videoLinks: LinkEntry[];
	onTeamsheetLinkChange: (index: number, value: string) => void;
	onVideoLinkChange: (index: number, value: string) => void;
	onAddTeamsheetLink: () => void;
	onAddVideoLink: () => void;
	onRemoveTeamsheetLink?: (index: number) => void;
	onRemoveVideoLink?: (index: number) => void;
	onSaveTeamsheetLink?: (index: number) => void;
	onSaveVideoLink?: (index: number) => void;
	isSaving?: boolean;
	showSaveIcon?: boolean;
	showRemoveIcon?: boolean;
}

function LinkRows({
	links,
	onChange,
	onAdd,
	addLabel,
	onRemove,
	onSaveLink,
	isSaving,
	showSaveIcon,
	showRemoveIcon,
}: {
	links: LinkEntry[];
	onChange: (index: number, value: string) => void;
	onAdd: () => void;
	addLabel: string;
	onRemove?: (index: number) => void;
	onSaveLink?: (index: number) => void;
	isSaving?: boolean;
	showSaveIcon?: boolean;
	showRemoveIcon?: boolean;
}) {
	const placeholder = _("discover.gamesTable.programGame.placeholder.unfilled");
	const hasInlineActions = (showSaveIcon && onSaveLink) || (showRemoveIcon && onRemove);
	const inputClassName = hasInlineActions
		? "discoverGameLinksFields-input discoverGameLinksFields-input--withActions"
		: "discoverGameLinksFields-input";

	return (
		<>
			{links.map((entry, index) => (
				<div className="discoverGameLinksFields-row" key={entry.id}>
					<div className="discoverGameLinksFields-inputWrap">
						<input
							type="text"
							className={inputClassName}
							placeholder={placeholder}
							value={entry.value}
							onChange={(event) => onChange(index, event.target.value)}
							disabled={isSaving}
						/>
						{showRemoveIcon && onRemove ? (
							<button
								type="button"
								className="discoverGameLinksFields-removeBtn"
								onClick={() => onRemove(index)}
								disabled={isSaving}
								aria-label={_("btn.delete")}
							>
								<img src={DeleteIcon} alt="" />
							</button>
						) : null}
						{showSaveIcon && onSaveLink ? (
							<button
								type="button"
								className="discoverGameLinksFields-saveBtn"
								onClick={() => onSaveLink(index)}
								disabled={isSaving}
								aria-label={_("discover.gameDetail.history.save")}
							>
								<img src={SaveIcon} alt="" />
							</button>
						) : null}
					</div>
				</div>
			))}
			<button type="button" className="discoverGameLinksFields-addLink" onClick={onAdd} disabled={isSaving}>
				<svg width="14" height="14" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
					<path d="M8 3V13M3 8H13" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
				</svg>
				{addLabel}
			</button>
		</>
	);
}

export function DiscoverGameLinksFields({
	teamsheetLinks,
	videoLinks,
	onTeamsheetLinkChange,
	onVideoLinkChange,
	onAddTeamsheetLink,
	onAddVideoLink,
	onRemoveTeamsheetLink,
	onRemoveVideoLink,
	onSaveTeamsheetLink,
	onSaveVideoLink,
	isSaving = false,
	showSaveIcon = false,
	showRemoveIcon = false,
}: DiscoverGameLinksFieldsProps) {
	return (
		<>
			<div className="discoverGameLinksFields-section">
				<label className="discoverGameLinksFields-label">
					{_("discover.gamesTable.programGame.field.teamsheetLinks")}
				</label>
				<LinkRows
					links={teamsheetLinks}
					onChange={onTeamsheetLinkChange}
					onAdd={onAddTeamsheetLink}
					addLabel={_("discover.gamesTable.programGame.action.addTeamsheetLink")}
					onRemove={onRemoveTeamsheetLink}
					onSaveLink={onSaveTeamsheetLink}
					isSaving={isSaving}
					showSaveIcon={showSaveIcon}
					showRemoveIcon={showRemoveIcon}
				/>
			</div>

			<div className="discoverGameLinksFields-section">
				<label className="discoverGameLinksFields-label">
					{_("discover.gamesTable.programGame.field.videoLinks")}
				</label>
				<LinkRows
					links={videoLinks}
					onChange={onVideoLinkChange}
					onAdd={onAddVideoLink}
					addLabel={_("discover.gamesTable.programGame.action.addVideoLink")}
					onRemove={onRemoveVideoLink}
					onSaveLink={onSaveVideoLink}
					isSaving={isSaving}
					showSaveIcon={showSaveIcon}
					showRemoveIcon={showRemoveIcon}
				/>
			</div>
		</>
	);
}
